import { useCallback, useEffect, useState } from 'react'

import { Pager } from '../../lib/Pager'
import { SortTh, useSort } from '../../lib/Sort'
import { useSession } from '../../lib/SessionContext'
import { SectionHead } from '../offsite/parts'
import type { Alias, Employee, Paged, RenameHints, Unmapped } from './types'
import { BASE, errorText, nf } from './types'

/**
 * จับคู่ "ชื่อในแอปนับก้าว" กับ "รหัสพนักงาน" — ตาราง walkrun_alias (DDL 009)
 *
 * ── ทำไมต้องมี ──────────────────────────────────────────────────────────────
 * ไฟล์ CSV ไม่มีรหัสพนักงาน มีแต่ชื่อที่ผู้ใช้ตั้งเอง ระบบจึงยึดชื่อเป็นตัวตน
 * พอเจ้าตัวแก้ชื่อในแอป — เติมอิโมจิ แก้คำผิด ย้ายสาขา — คนเดียวจะโผล่สองบรรทัด
 * บนอันดับ และจำนวนผู้เข้าร่วมเกินจริง (เกิดจริง 6 คนภายในวันเดียว)
 *
 * ── ทำไมเป็นการจับคู่ ไม่ใช่ "รวมสองแถวเข้าด้วยกัน" ──────────────────────
 * การรวมต้องย้ายข้อมูลจริงและย้อนกลับไม่ได้ ส่วนตารางจับคู่เป็นแค่คำอธิบายทับ
 * ข้อมูลดิบ — **ไม่แตะ walkrun_day เลย** จับผิดคนก็แก้แถวเดียวจบ
 *
 * ── 1 คน หลายชื่อ ──────────────────────────────────────────────────────────
 * ตั้งใจให้พนักงานคนเดียวมีได้หลายชื่อ (เปลี่ยนกี่รอบก็จับคู่เพิ่ม) ตอนคิดอันดับ
 * ระบบเอา **ค่ามากสุดของแต่ละวัน** ข้ามทุกชื่อของคนนั้นแล้วค่อยบวกข้ามวัน
 * ไม่ใช่บวกทุกแถว — วันที่ทับกันของชื่อเก่า/ใหม่คือก้าววันเดียวกันที่ export
 * คนละรอบ บวกกันคือนับซ้ำ (เจอจริง: 2,819 + 7,651 ควรได้ 7,651 ไม่ใช่ 10,470)
 */
export function MappingCard({
  writable,
  reloadKey,
  onChanged,
}: {
  writable: boolean
  /** เปลี่ยนค่าเมื่อ import เสร็จ → โหลดรายการใหม่ */
  reloadKey: number
  onChanged: () => Promise<void>
}) {
  const { call } = useSession()
  const [unmapped, setUnmapped] = useState<Paged<{ unmapped: Unmapped[] }> | null>(null)
  const [aliases, setAliases] = useState<Paged<{ aliases: Alias[] }> | null>(null)
  const [hints, setHints] = useState<RenameHints | null>(null)
  /* โหลดครั้งเดียวต่อการ์ด ไม่ใช่ต่อแถว — 93 คนก้อนเดียว เบากว่ายิงค้นทีละแถว 10 รอบ */
  const [staff, setStaff] = useState<Employee[]>([])
  const [page, setPage] = useState(1)
  /* สองตารางในการ์ดนี้เรียงแยกกัน — ตารางที่ยังไม่จับคู่กับตารางที่จับแล้ว
     เป็นคนละงาน ใช้ state ร่วมกันแล้วกดเรียงอันหนึ่งอีกอันจะพลิกตามโดยไม่มีเหตุผล */
  const uSort = useSort([{ key: 'steps', dir: 'desc' }], () => setPage(1))
  const aSort = useSort([{ key: 'steps', dir: 'desc' }])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [open, setOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [u, a, h, e] = await Promise.all([
        call<Paged<{ unmapped: Unmapped[] }>>(
          `${BASE}/unmapped?page=${page}&pageSize=10&sort=${encodeURIComponent(uSort.param)}`,
        ),
        call<Paged<{ aliases: Alias[] }>>(
          `${BASE}/aliases?pageSize=100&sort=${encodeURIComponent(aSort.param)}`,
        ),
        call<RenameHints>(`${BASE}/renames`),
        call<{ employees: Employee[] }>(`${BASE}/employees`),
      ])
      setUnmapped(u)
      setAliases(a)
      setHints(h)
      setStaff(e.employees)
      setError(null)
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }, [call, page, uSort.param, aSort.param])

  useEffect(() => {
    void load()
  }, [load, reloadKey])

  async function save(nameKey: string, emp: Employee) {
    setError(null)
    try {
      await call(`${BASE}/alias`, {
        method: 'POST',
        body: JSON.stringify({
          nameKey,
          empCode: emp.empCode,
          empName: emp.empName,
          department: emp.department,
        }),
      })
      setNotice(`จับคู่กับ ${emp.empCode} ${emp.empName} แล้ว`)
      await load()
      await onChanged()
    } catch (err) {
      setError(errorText(err))
    }
  }

  async function unlink(nameKey: string) {
    setError(null)
    try {
      await call(`${BASE}/alias/remove`, { method: 'POST', body: JSON.stringify({ nameKey }) })
      setNotice('ถอดการจับคู่แล้ว — ชื่อนี้กลับไปเป็นคนแยกบนอันดับ')
      await load()
      await onChanged()
    } catch (err) {
      setError(errorText(err))
    }
  }

  const left = unmapped?.total ?? 0
  const mapped = aliases?.aliases ?? []
  const multi = mapped.filter((a) => a.nameCount > 1)

  return (
    <section className="card">
      <SectionHead
        title="จับคู่ชื่อในแอปกับพนักงาน"
        meta={
          unmapped
            ? `จับคู่แล้ว ${nf.format(aliases?.total ?? 0)} คน · ยังไม่จับคู่ ${nf.format(left)} ชื่อ`
            : undefined
        }
        onReload={() => void load()}
        loading={loading}
      />

      <p className="meta">
        ไฟล์ CSV ไม่มีรหัสพนักงาน ระบบจึงใช้<strong>ชื่อในแอป</strong>เป็นตัวตน —
        พอเจ้าตัวแก้ชื่อ (เติมอิโมจิ / แก้คำผิด / ย้ายสาขา) จะกลายเป็นคนใหม่ทันที
        จับคู่กับรหัสพนักงานแล้วทุกชื่อของคนเดียวกันจะรวมเป็นบรรทัดเดียวบนอันดับ ·{' '}
        <strong>1 คนมีได้หลายชื่อ</strong> · ข้อมูลก้าวไม่ถูกแตะ ถอดคู่กลับได้ตลอด
      </p>

      {error && <p className="error">{error}</p>}
      {notice && <p className="ok">{notice}</p>}

      {multi.length > 0 && (
        <p className="ok">
          รวมชื่อให้ {multi.length} คนแล้ว:{' '}
          {multi.map((a) => `${a.empName ?? a.empCode} (${a.nameCount} ชื่อ)`).join(' · ')}
        </p>
      )}

      {/* ชี้เป้าว่าควรจับคู่ใครก่อน — ชื่อที่หายไปจากไฟล์คือคนเดิมที่เปลี่ยนชื่อ
          ไม่ใช่คนที่ออกจากโครงการ (ส่วนใหญ่) จับคู่ทั้งชื่อเก่าและชื่อใหม่กับ
          พนักงานคนเดียวกัน แล้วอันดับจะรวมให้เอง */}
      {hints && hints.missing.length > 0 && (
        <p className="warn">
          <strong>{hints.missing.length} ชื่อหายไปจากไฟล์ล่าสุด</strong> — น่าจะเป็นคนเดิมที่เปลี่ยนชื่อ
          ไม่ใช่คนที่ออกจากโครงการ:{' '}
          {hints.missing.map((m) => `${m.name} (${nf.format(m.steps)})`).join(' · ')}
          <br />
          วิธีแก้: จับคู่<strong>ทั้งชื่อเก่าและชื่อใหม่</strong>กับพนักงานคนเดียวกัน
          ระบบจะรวมให้เป็นบรรทัดเดียวและไม่นับก้าววันที่ทับกันซ้ำ
        </p>
      )}

      {hints && hints.twins.length > 0 && (
        <p className="meta">
          อีก {hints.twins.length} คนเปลี่ยนชื่อโดย<strong>เติมอิโมจิอย่างเดียว</strong> (
          {hints.twins.map((t) => t.name).join(', ')}) — ฐานข้อมูลรวมให้เองแล้ว ไม่ต้องจับคู่
        </p>
      )}

      {left > 0 ? (
        <p className="warn">
          ยังมี <strong>{nf.format(left)} ชื่อ</strong>ที่ยังไม่รู้ว่าเป็นพนักงานคนไหน —
          อันดับยังใช้ได้ (ระบบใช้ชื่อในแอปไปก่อน) แต่ถ้าคนไหนเปลี่ยนชื่อระหว่างทาง
          จะยังนับเป็นสองคนจนกว่าจะจับคู่ · เริ่มจากคนที่ก้าวเยอะที่สุดก่อนจะคุ้มที่สุด
        </p>
      ) : (
        <p className="ok">จับคู่ครบทุกชื่อแล้ว</p>
      )}

      <p>
        <button type="button" className="ghost" onClick={() => setOpen((v) => !v)}>
          {open ? 'ซ่อนรายการ' : `จัดการการจับคู่${left > 0 ? ` (เหลือ ${nf.format(left)})` : ''}`}
        </button>
      </p>

      {open && (
        <>
          <h3>ยังไม่จับคู่</h3>
          {!unmapped ? (
            <p className="meta">กำลังโหลด…</p>
          ) : unmapped.unmapped.length === 0 ? (
            <p className="meta">ไม่มีชื่อที่ค้างอยู่</p>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <SortTh label="ชื่อในแอป" sortKey="name" sort={uSort.sort} onToggle={uSort.toggle} />
                    <SortTh label="ก้าวสะสม" sortKey="steps" sort={uSort.sort} onToggle={uSort.toggle} className="nowrap" />
                    <SortTh label="วัน" sortKey="dayCount" sort={uSort.sort} onToggle={uSort.toggle} className="nowrap" />
                    {writable && <th>เป็นพนักงานคนไหน</th>}
                  </tr>
                </thead>
                <tbody>
                  {unmapped.unmapped.map((u) => (
                    <UnmappedRow key={u.nameKey} row={u} staff={staff} writable={writable} onPick={save} />
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {unmapped && (
            <Pager
              page={unmapped.page}
              pageSize={unmapped.pageSize}
              total={unmapped.total}
              loading={loading}
              onPage={setPage}
              unit="ชื่อ"
            />
          )}

          {mapped.length > 0 && (
            <>
              <h3>จับคู่แล้ว</h3>
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <SortTh label="พนักงาน" sortKey="empName" sort={aSort.sort} onToggle={aSort.toggle} />
                      <SortTh label="ชื่อในแอปที่ใช้" sortKey="nameCount" sort={aSort.sort} onToggle={aSort.toggle} />
                      <SortTh label="ก้าวสะสม" sortKey="steps" sort={aSort.sort} onToggle={aSort.toggle} className="nowrap" />
                    </tr>
                  </thead>
                  <tbody>
                    {mapped.map((a) => (
                      <tr key={a.empCode}>
                        <td>
                          <strong>{a.empCode}</strong> {a.empName ?? ''}
                          <div className="meta">
                            {a.department ?? '-'}
                            {a.nameCount > 1 && ` · รวม ${a.nameCount} ชื่อ`}
                          </div>
                        </td>
                        <td>
                          {a.names.map((n, i) => (
                            <div key={a.nameKeys[i] ?? n} className="row-actions">
                              <span>{n}</span>
                              {writable && (
                                <button
                                  type="button"
                                  className="ghost tiny"
                                  onClick={() => void unlink(a.nameKeys[i])}
                                >
                                  ถอด
                                </button>
                              )}
                            </div>
                          ))}
                        </td>
                        <td className="nowrap">{nf.format(a.steps)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </section>
  )
}

/**
 * หนึ่งแถวของชื่อที่ยังไม่จับคู่ + dropdown เลือกพนักงาน
 *
 * ⚠️ ต้องเลือกจากรายการ ไม่ให้พิมพ์รหัสเอง — พิมพ์ผิดหลักเดียวคือก้าวไปโผล่
 *    ใต้ชื่อคนอื่นโดยไม่มีอะไรฟ้อง (ไม่มี FK ข้ามฐานไป HRMOnline)
 *
 * ── ทำไมคนที่จับคู่แล้วยังอยู่ใน dropdown ────────────────────────────────
 * **1 พนักงานมีได้หลายชื่อโดยตั้งใจ** — คนเปลี่ยนชื่อในแอปกี่รอบก็จับคู่เพิ่มได้
 * ถ้าซ่อนคนที่จับคู่แล้วออกไป จะจับคู่ชื่อที่สองของคนเดิมไม่ได้เลย ซึ่งเป็นเหตุผลหลัก
 * ที่ฟีเจอร์นี้มีอยู่ (เมธาฟ้าแว๊บๆ @LBK กับ @LKB ต้องชี้ไปคนเดียวกัน)
 * → แยกเป็นสองกลุ่มแทน: ยังไม่จับคู่ขึ้นก่อน คนที่จับคู่แล้วอยู่กลุ่มล่างพร้อมบอกว่ากี่ชื่อ
 */
function UnmappedRow({
  row,
  staff,
  writable,
  onPick,
}: {
  row: Unmapped
  staff: Employee[]
  writable: boolean
  onPick: (nameKey: string, emp: Employee) => Promise<void>
}) {
  const [picked, setPicked] = useState('')
  const [busy, setBusy] = useState(false)

  const fresh = staff.filter((e) => e.aliasCount === 0)
  const used = staff.filter((e) => e.aliasCount > 0)

  async function save() {
    const emp = staff.find((e) => e.empCode === picked)
    if (!emp) return
    setBusy(true)
    try {
      await onPick(row.nameKey, emp)
    } finally {
      setBusy(false)
    }
  }

  return (
    <tr>
      <td>
        <strong>{row.name}</strong>
        <div className="meta">ล่าสุด {row.lastDay ?? '-'}</div>
      </td>
      <td className="nowrap">{nf.format(row.steps)}</td>
      <td className="nowrap">{row.dayCount}</td>
      {writable && (
        <td>
          <div className="row-actions">
            <select
              className="pick-emp"
              value={picked}
              onChange={(e) => setPicked(e.target.value)}
              disabled={busy || staff.length === 0}
              aria-label={`เลือกพนักงานสำหรับ ${row.name}`}
            >
              <option value="">— เลือกพนักงาน —</option>
              <optgroup label="ยังไม่จับคู่">
                {fresh.map((e) => (
                  <option key={e.empCode} value={e.empCode}>
                    {e.empCode} · {e.empName}
                  </option>
                ))}
              </optgroup>
              {used.length > 0 && (
                <optgroup label="จับคู่แล้ว — เลือกได้ถ้าเป็นคนเดิมที่เปลี่ยนชื่อ">
                  {used.map((e) => (
                    <option key={e.empCode} value={e.empCode}>
                      {e.empCode} · {e.empName} ({e.aliasCount} ชื่อ)
                    </option>
                  ))}
                </optgroup>
              )}
            </select>
            <button type="button" disabled={!picked || busy} onClick={() => void save()}>
              {busy ? 'กำลังบันทึก…' : 'จับคู่'}
            </button>
          </div>
        </td>
      )}
    </tr>
  )
}
